import { useCallback, useEffect, useState } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// useInstallPrompt
//
// Chrome-family browsers fire `beforeinstallprompt` when the app meets the
// installability bar (manifest + service worker + HTTPS). The event must be
// captured and its default prevented, or the browser shows its own mini
// infobar and the saved event becomes unusable.
//
// Returns `canInstall: false` on iOS, which never fires this event at all —
// there, installing is Share → Add to Home Screen and no API can trigger it.
// Rather than show a button that cannot work, callers render nothing.
// ─────────────────────────────────────────────────────────────────────────────

export function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);

  useEffect(() => {
    // Already running as an installed app — nothing to offer.
    const standalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true;
    if (standalone) return undefined;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => setDeferred(null);

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (e) {
      console.error("[pwa] install prompt failed:", e);
    }
    // The saved event is single-use whether they accepted or dismissed.
    setDeferred(null);
  }, [deferred]);

  return { canInstall: Boolean(deferred), promptInstall };
}
